export function deepcopy<T>(value: T): T {
  if (value == null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    const result = new Array(value.length);
    for (let index = 0; index < value.length; index++) {
      const item = value[index];
      result[index] = item && typeof item === 'object' ? deepcopy(item) : item;
    }
    return result as T;
  }

  const proto = Object.getPrototypeOf(value);
  if (proto === Object.prototype || proto === null) {
    const sourceObj = value as Record<string, unknown>;
    const resultObj: Record<string, unknown> = proto === null ? Object.create(null) : {};

    for (const key in sourceObj) {
      if (Object.prototype.hasOwnProperty.call(sourceObj, key)) {
        const item = sourceObj[key];
        resultObj[key] = item && typeof item === 'object' ? deepcopy(item) : item;
      }
    }
    return resultObj as T;
  }

  const ctor = (value as { constructor: new (...args: unknown[]) => unknown }).constructor;

  if (ctor === String || ctor === Number || ctor === Boolean) {
    return ctor(value as never) as T;
  }

  if (ctor === Date) {
    return new Date(value as Date) as T;
  }

  if (ctor === Map) {
    const map = value as Map<unknown, unknown>;
    const result = new Map<unknown, unknown>();
    for (const [key, item] of map) {
      result.set(key, item && typeof item === 'object' ? deepcopy(item) : item);
    }
    return result as T;
  }

  if (ctor === Set) {
    const set = value as Set<unknown>;
    const result = new Set<unknown>();
    for (const item of set) {
      result.add(item && typeof item === 'object' ? deepcopy(item) : item);
    }
    return result as T;
  }

  if (ctor === RegExp) {
    const regexp = value as RegExp;
    const clone = new RegExp(regexp.source, regexp.flags);
    clone.lastIndex = regexp.lastIndex;
    return clone as T;
  }

  if (ArrayBuffer.isView(value)) {
    if (ctor === DataView) {
      const dataView = value as DataView;
      const bufferCopy = dataView.buffer.slice(dataView.byteOffset, dataView.byteOffset + dataView.byteLength);
      return new DataView(bufferCopy) as T;
    }
    return new (ctor as new (source: ArrayBufferView) => ArrayBufferView)(value as ArrayBufferView) as T;
  }

  if (ctor === ArrayBuffer) {
    return (value as ArrayBuffer).slice(0) as T;
  }

  if (
    ctor === Error ||
    ctor === RangeError ||
    ctor === EvalError ||
    ctor === ReferenceError ||
    ctor === SyntaxError ||
    ctor === TypeError ||
    ctor === URIError
  ) {
    return value;
  }

  if (ctor === Promise || typeof value === 'symbol' || ctor === WeakMap || ctor === WeakSet) {
    return value;
  }

  const resultFallback = Object.create(proto) as Record<string, unknown>;
  const source = value as Record<string, unknown>;
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const item = source[key];
      resultFallback[key] = item && typeof item === 'object' ? deepcopy(item) : item;
    }
  }
  return resultFallback as T;
}

export function isObject(item: unknown): item is Record<string, unknown> {
  return Boolean(item && typeof item === 'object' && !Array.isArray(item));
}

export function applyUpdates(source: Record<string, unknown>, incoming: Record<string, unknown>): void {
  for (const key in incoming) {
    if (key in source) {
      if (isObject(source[key]) && isObject(incoming[key])) {
        applyUpdates(source[key], incoming[key]);
      } else {
        source[key] = incoming[key];
      }
    }
  }
}

export function checkIfPureObject(element: unknown): element is Record<string, unknown> {
  return element !== null && typeof element === 'object' && !Array.isArray(element);
}

export function deepEqual(obj1: unknown, obj2: unknown): boolean {
  if (obj1 === obj2) {
    return true;
  }

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
    return false;
  }

  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) {
      return false;
    }
    for (let index = 0; index < obj1.length; index++) {
      if (!deepEqual(obj1[index], obj2[index])) {
        return false;
      }
    }
  } else {
    const record1 = obj1 as Record<string, unknown>;
    const record2 = obj2 as Record<string, unknown>;
    const keys1 = Object.keys(record1);
    const keys2 = Object.keys(record2);

    if (keys1.length !== keys2.length) {
      return false;
    }
    for (const key of keys1) {
      if (!keys2.includes(key)) {
        return false;
      }
      if (!deepEqual(record1[key], record2[key])) {
        return false;
      }
    }
  }

  return true;
}

export function getValueFromPath(obj: Record<string, unknown>, path: string): unknown {
  const keys = path.match(/([^[.\]]+)/g) ?? [];
  return keys.reduce<unknown>((acc, key) => {
    if (acc != null && typeof acc === 'object') {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

export function setValueFromPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.');
  const lastKey = keys.pop();
  if (!lastKey) return;

  const target = keys.reduce<Record<string, unknown>>((acc, key) => {
    if (!acc[key] || typeof acc[key] !== 'object') {
      acc[key] = {};
    }
    return acc[key] as Record<string, unknown>;
  }, obj);
  target[lastKey] = value;
}

export function removeLastKey(path: string): string {
  const lastDotIndex = path.lastIndexOf('.');
  return path.substring(0, lastDotIndex + 1);
}

export function translatePathIndices(path: string): string {
  return path.replace(/\[(\d+)\]/g, (_match, index) => `[${parseInt(index, 10) - 1}]`);
}

export function setNestedDeclarationValue(obj: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.');
  let current: Record<string, unknown> | unknown[] = obj;

  for (let index = 0; index < keys.length - 1; index++) {
    const key = keys[index];
    const arrayMatch = key.match(/(\w+)\[(\d+)\]/);

    if (arrayMatch) {
      const arrayKey = arrayMatch[1];
      const arrayIndex = parseInt(arrayMatch[2], 10);
      const currentRecord = current as Record<string, unknown>;
      if (!currentRecord[arrayKey]) {
        currentRecord[arrayKey] = [];
      }
      current = (currentRecord[arrayKey] as unknown[])[arrayIndex] as Record<string, unknown>;
    } else {
      const currentRecord = current as Record<string, unknown>;
      if (!currentRecord[key]) {
        currentRecord[key] = {};
      }
      current = currentRecord[key] as Record<string, unknown>;
    }
  }

  const lastKey = keys[keys.length - 1];
  (current as Record<string, unknown>)[lastKey] = value;
}

export function getNestedDeclarationValue(obj: Record<string, unknown>, path: string): unknown {
  const keys = path.split('.');
  let current: Record<string, unknown> | unknown[] = obj;

  for (let index = 0; index < keys.length - 1; index++) {
    const key = keys[index];
    const arrayMatch = key.match(/(\w+)\[(\d+)\]/);

    if (arrayMatch) {
      const arrayKey = arrayMatch[1];
      const arrayIndex = parseInt(arrayMatch[2], 10);
      const currentRecord = current as Record<string, unknown>;
      if (!currentRecord[arrayKey]) {
        currentRecord[arrayKey] = [];
      }
      current = (currentRecord[arrayKey] as unknown[])[arrayIndex] as Record<string, unknown>;
    } else {
      const currentRecord = current as Record<string, unknown>;
      if (!currentRecord[key]) {
        currentRecord[key] = {};
      }
      current = currentRecord[key] as Record<string, unknown>;
    }
  }

  const lastKey = keys[keys.length - 1];
  return (current as Record<string, unknown>)[lastKey];
}

export function shouldOverrideData(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  pathMappings: Record<string, string>
): boolean {
  return Object.entries(pathMappings).some(([sourceKey, targetPath]) => {
    return (
      source[sourceKey] &&
      getValueFromPath(target, targetPath) &&
      source[sourceKey] !== getValueFromPath(target, targetPath)
    );
  });
}

export function isEmptyObject(obj: unknown): boolean {
  if (!obj || typeof obj !== 'object') return true;

  return Object.values(obj as Record<string, unknown>).every((value) => {
    if (value === null || value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return isEmptyObject(value);
    if (typeof value === 'string') return value.trim() === '';
    if (typeof value === 'number') return value === 0;
    return false;
  });
}

export function isEmpty(data: unknown): boolean {
  if (Array.isArray(data)) {
    return data.length === 0;
  }
  if (typeof data === 'object' && data !== null) {
    return isEmptyObject(data);
  }
  return !data;
}
